import { yupResolver } from '@hookform/resolvers/yup'
import { TFunction } from 'i18next'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ObjectSchema, object } from 'yup'

import { emailSchema, useMetaResponseHandler } from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'
import { formTranslation } from '@/Shared/ui/sharedTranslation'
import { UserContract } from '@/User/application'
import { EmailChangeDataForm, UserRepository } from '@/User/domain'
import { userDataTranslation } from '@/User/ui/userTranslation'

interface IUseChangePassword {
  form: UseFormReturn<EmailChangeDataForm>
  save: (data: EmailChangeDataForm) => Promise<boolean>
}

const formValidationScheme = (t: TFunction): ObjectSchema<EmailChangeDataForm> =>
  object({
    email: emailSchema(t, true).required(formTranslation.errors.fieldRequired),
  })

export const useChangeEmail = (
  repository: UserRepository,
  currentEmail: string,
): IUseChangePassword => {
  const { startLoading, stopLoading } = useLoader()
  const { t } = useTranslation()
  const { handleMetaResponse } = useMetaResponseHandler()

  const form = useForm<EmailChangeDataForm>({
    resolver: yupResolver(formValidationScheme(t)),
    mode: 'onChange',
    defaultValues: {
      email: currentEmail,
    },
  })

  const save = async (data: EmailChangeDataForm): Promise<boolean> => {
    if (data.email === currentEmail) return true

    startLoading()

    const response = await UserContract(repository).changeEmail(data)

    stopLoading()

    const valid = handleMetaResponse(response?.meta, form, {
      successMessage: t(userDataTranslation.success),
    })

    if (valid) {
      form.reset({
        ...form.getValues(),
      })
    }

    return valid
  }

  return { form, save }
}
